import React, { useState } from 'react'
import { alpha, Fade, SvgIcon, Typography, Box, IconButton, Stack } from '@mui/material'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { ReactComponent as ArrowDownIcon } from '../../../../../../../assets/images/arrows/down.svg'
import { ReactComponent as RightArrowIcon } from '../../../../../../../assets/images/arrows/right.svg'
import { ValueNarrative, ValueNarrativeCategory } from '../../../../../queries/useGetValueNarrativesQuery'
import { useValueNarrativeColorBySubpopulation } from '../../../../../../../views/ValueCompendium/tabs/ValueNarrativesTab/hooks/useValueNarrativeColorBySubpopulation'
import ValueNarrativeArchiveIcon from '../../../../../components/ValueNarrativeIcon/ValueNarrativeArchiveIcon'
import ValueNarrativeLockIcon from '../../../../../components/ValueNarrativeIcon/ValueNarrativeLockIcon'
import ValueNarrativeActions from '../../../../ValueNarrativeActions/ValueNarrativeActions'

const MIN_DRAG_DISTANCE_TO_ALLOW_CLICK = 1

type CardViewProps = {
    category: ValueNarrativeCategory
    handleSelectNarrative: (categoryId: string, valueNarrativeId: string) => void
    isReorderModeActive?: boolean
    onReorderNarratives?: (newNarratives: ValueNarrative[]) => void
}

const CardView = ({ category, handleSelectNarrative, isReorderModeActive, onReorderNarratives }: CardViewProps) => {
    const [areNarrativesVisible, setAreNarrativesVisible] = useState(!category.isPseudoCategory)

    const { getValueNarrativeColorBySubpopulation } = useValueNarrativeColorBySubpopulation()

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: MIN_DRAG_DISTANCE_TO_ALLOW_CLICK } })
    )

    const handleSortNarratives = (event) => {
        const { active, over } = event

        if (!over || active.id === over.id) {
            return
        }

        const indexOfDestinationCard = event.over.data.current.sortable.index
        const indexOfSourceCard = event.active.data.current.sortable.index
        const newNarratives = arrayMove(category.narratives, indexOfSourceCard, indexOfDestinationCard)
        onReorderNarratives?.(newNarratives)
    }

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                width="max-content"
                gap={1}
                mb={1}
                color={category.isPseudoCategory ? 'figma.grayscale.tier4' : 'figma.grayscale.textHier1'}
                sx={
                    category.isPseudoCategory
                        ? {
                              cursor: 'pointer',
                              svg: {
                                  pointerEvents: 'none',
                              },
                          }
                        : {}
                }
                onClick={() => {
                    if (category.isPseudoCategory) setAreNarrativesVisible(!areNarrativesVisible)
                }}
            >
                <Typography color="inherit" fontSize="18px !important" fontWeight={400}>
                    {category.name}
                </Typography>
                {category.isPseudoCategory && (
                    <SvgIcon
                        component={ArrowDownIcon}
                        inheritViewBox
                        color="inherit"
                        sx={{
                            fontSize: '16px',
                            transform: areNarrativesVisible ? 'rotate(180deg)' : '',
                        }}
                    />
                )}
            </Stack>

            <Fade in={areNarrativesVisible} mountOnEnter unmountOnExit timeout={1000}>
                <Box
                    display="grid"
                    gridTemplateColumns={{
                        sm: 'repeat(1, 1fr)',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr)',
                    }}
                    gridAutoRows="minmax(80px, auto)"
                    gap={3}
                    pb={2}
                >
                    <DndContext sensors={sensors} onDragEnd={handleSortNarratives}>
                        <SortableContext
                            items={category.narratives.map((narrative) => narrative._id)}
                            strategy={rectSortingStrategy}
                            disabled={!isReorderModeActive}
                        >
                            {category.narratives.map((narrative) => {
                                const colorBySubpopulation = getValueNarrativeColorBySubpopulation(
                                    narrative.subpopulation
                                )

                                const onValueNarrativeClick = () => {
                                    handleSelectNarrative(narrative.categoryId, narrative._id)
                                }

                                return (
                                    <SortableNarrativeCard
                                        key={narrative._id}
                                        narrative={narrative}
                                        colorBySubpopulation={colorBySubpopulation}
                                        isReorderModeActive={!!isReorderModeActive}
                                        onValueNarrativeClick={onValueNarrativeClick}
                                    />
                                )
                            })}
                        </SortableContext>
                    </DndContext>
                </Box>
            </Fade>
        </Box>
    )
}

type SortableNarrativeCardProps = {
    narrative: ValueNarrative
    colorBySubpopulation: string
    isReorderModeActive: boolean
    onValueNarrativeClick: () => void
}

const SortableNarrativeCard = ({
    narrative,
    colorBySubpopulation,
    isReorderModeActive,
    onValueNarrativeClick,
}: SortableNarrativeCardProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: narrative._id })

    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            p={2}
            pb={1.5}
            position="relative"
            sx={(theme) => ({
                backgroundColor: `${alpha(colorBySubpopulation, 0.1)} !important`,
                borderColor: colorBySubpopulation,
                borderRadius: '12px',
                borderStyle: 'solid',
                borderWidth: '1px',
                cursor: isReorderModeActive ? 'grab' : 'default',
                // Deliberately translate-only (no CSS.Transform.toString, which
                // also applies dnd-kit's scaleX/scaleY -- that stretches/squishes
                // the card's content when swapping with a differently-sized one).
                transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
                transition,
                zIndex: transform ? 1000 : 'auto',
            })}
        >
            <Typography
                mb={1}
                fontSize="16px !important"
                fontWeight={400}
                color="figma.grayscale.textHier2"
                sx={{
                    paddingRight: 6,
                    overflow: 'auto',
                    maxHeight: '350px',
                }}
            >
                {narrative.name}
            </Typography>
            {!!narrative.archived && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    position="absolute"
                    top="12px"
                    right="12px"
                >
                    <ValueNarrativeArchiveIcon
                        archived={narrative.archived}
                        sx={{
                            fontSize: '24px',
                        }}
                    />
                </Stack>
            )}
            {!narrative.archived && !!narrative.locked && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    position="absolute"
                    top="12px"
                    right="12px"
                >
                    <ValueNarrativeLockIcon
                        locked={narrative.locked}
                        sx={{
                            fontSize: '24px',
                        }}
                    />
                </Stack>
            )}
            <Stack direction="row" alignItems="center" position="absolute" bottom="12px" right="12px">
                <ValueNarrativeActions valueNarrative={narrative} />
                <IconButton
                    size="small"
                    onClick={onValueNarrativeClick}
                    sx={{
                        cursor: 'pointer',
                    }}
                >
                    <SvgIcon
                        component={RightArrowIcon}
                        inheritViewBox
                        fontSize="small"
                        sx={{
                            fontSize: '16px',
                            color: 'figma.primary',
                        }}
                    />
                </IconButton>
            </Stack>
        </Box>
    )
}

export default CardView
